/**
 * Footer component - Site footer
 */

import {Link} from 'react-router-dom';
import {Container} from '../ui/Container';
import {ThemeToggle} from '../ui/ThemeToggle';
import {ROUTES} from '../../config/constants';

interface FooterLinkGroup {
  title: string;
  links: {label: string; href: string}[];
}

const footerLinks: FooterLinkGroup[] = [
  {
    title: 'Product',
    links: [
      {label: 'Features', href: '#features'},
      {label: 'Pricing', href: ROUTES.PRICING},
      {label: 'Enterprise', href: ROUTES.ENTERPRISE},
      {label: 'Integrations', href: ROUTES.INTEGRATIONS},
    ],
  },
  {
    title: 'Company',
    links: [
      {label: 'About', href: ROUTES.ABOUT},
      {label: 'Blog', href: ROUTES.BLOG},
      {label: 'Careers', href: ROUTES.CAREERS},
      {label: 'Contact', href: ROUTES.CONTACT},
    ],
  },
  {
    title: 'Resources',
    links: [
      {label: 'Documentation', href: '/docs'},
      {label: 'Help Center', href: ROUTES.HELP_CENTER},
      {label: 'API', href: ROUTES.API},
      {label: 'Security', href: ROUTES.SECURITY},
    ],
  },
  {
    title: 'Legal',
    links: [
      {label: 'Privacy', href: ROUTES.PRIVACY},
      {label: 'Terms', href: ROUTES.TERMS},
      {label: 'Security', href: ROUTES.SECURITY},
    ],
  },
];

const socialLinks = [
  {name: 'Twitter', href: '#'},
  {name: 'LinkedIn', href: '#'},
  {name: 'GitHub', href: '#'},
];

export function Footer() {
  return (
    <footer className="bg-backgroundSecondary dark:bg-[#1E1E1E] border-t border-border dark:border-gray-700">
      <Container className="py-12 sm:py-16">
        <div className="grid grid-cols-2 md:grid-cols-4 lg:grid-cols-5 gap-8 mb-12">
          {/* Logo */}
          <div className="col-span-2 md:col-span-1">
            <Link to={ROUTES.HOME} className="flex items-center space-x-2 mb-4">
              <div className="w-8 h-8 bg-gray-900 dark:bg-orange-600 rounded-lg flex items-center justify-center">
                <span className="text-white font-bold text-lg">V</span>
              </div>
              <span className="font-heading font-bold text-xl text-textPrimary dark:text-gray-100">VoxaPilot</span>
            </Link>
            <p className="text-sm text-textSecondary dark:text-gray-400">
              Meeting intelligence for modern teams
            </p>
          </div>

          {/* Link Groups */}
          {footerLinks.map((group) => (
            <div key={group.title}>
              <h4 className="font-semibold text-textPrimary dark:text-gray-100 mb-4">{group.title}</h4>
              <ul className="space-y-2">
                {group.links.map((link) => (
                  <li key={link.label}>
                    <Link
                      to={link.href}
                      className="text-sm text-textSecondary hover:text-orange-600 dark:hover:text-orange-500 transition-colors"
                    >
                      {link.label}
                    </Link>
                  </li>
                ))}
              </ul>
            </div>
          ))}
        </div>

        {/* Security Badges */}
        <div className="pt-8 border-t border-border mb-8">
          <div className="flex flex-col sm:flex-row justify-between items-center gap-6">
            <div>
              <p className="text-sm text-textTertiary dark:text-gray-400 mb-4 text-center sm:text-left">
                Enterprise-Grade Security
              </p>
              <div className="flex flex-wrap justify-center sm:justify-start items-center gap-3">
                {['SOC 2', 'GDPR', 'HIPAA', 'ISO 27001'].map((cert) => (
                  <Link
                    key={cert}
                    to={ROUTES.SECURITY}
                    className="px-3 py-1.5 text-xs font-medium text-textSecondary dark:text-[#CCCCCC] bg-white dark:bg-[#252526] border border-gray-200 dark:border-gray-700 rounded-md hover:border-gray-300 dark:hover:border-gray-600 transition-colors"
                  >
                    {cert}
                  </Link>
                ))}
              </div>
            </div>
            <Link
              to={ROUTES.SECURITY}
              className="text-sm text-orange-600 dark:text-orange-500 hover:text-orange-700 dark:hover:text-orange-400 font-medium"
            >
              Learn more about security →
            </Link>
          </div>
        </div>

        {/* Bottom */}
        <div className="pt-8 border-t border-border flex flex-col sm:flex-row justify-between items-center gap-4">
          <p className="text-sm text-textTertiary dark:text-gray-400">
            © {new Date().getFullYear()} VoxaPilot. All rights reserved.
          </p>
          <div className="flex flex-col sm:flex-row items-center gap-4 sm:gap-6">
            {/* Theme Toggle */}
            <div className="flex items-center gap-2">
              <span className="text-sm text-textSecondary hidden sm:inline">Theme:</span>
              <ThemeToggle />
            </div>
            <div className="flex items-center space-x-6">
              {socialLinks.map((social) => (
                <a
                  key={social.name}
                  href={social.href}
                  className="text-sm text-textSecondary hover:text-textPrimary transition-colors"
                  aria-label={social.name}
                >
                  {social.name}
                </a>
              ))}
            </div>
          </div>
        </div>
      </Container>
    </footer>
  );
}

